import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Password policy values mirrored from backend/app/core/password_policy.py.
const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 64;
const PASSWORD_BLANK = 'PASSWORD_BLANK';
const PASSWORD_TOO_SHORT = 'PASSWORD_TOO_SHORT';
const PASSWORD_TOO_LONG = 'PASSWORD_TOO_LONG';

// Frontend-only validation code for the confirm password field.
const PASSWORD_MISMATCH = 'PASSWORD_MISMATCH';

// User-facing copy for password validation failures.
const PASSWORD_ERROR_MESSAGES = {
  [PASSWORD_BLANK]: 'Password cannot be blank.',
  [PASSWORD_TOO_SHORT]: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
  [PASSWORD_TOO_LONG]: `Password must be no more than ${MAX_PASSWORD_LENGTH} characters.`,
  [PASSWORD_MISMATCH]: 'Passwords do not match.',
};

/**
 * Extracts a readable invite registration error from FastAPI responses.
 * FastAPI can return detail as a string or as a validation error array.
 */
const getRegisterErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((item) => item.msg).filter(Boolean).join(' ') || 'Please check your registration details and try again.';
  }

  return error.response?.data?.message || 'Unable to complete registration. Please request a new invite.';
};

/**
 * Validates the password and confirmation fields before submitting to the backend.
 */
const validatePassword = (password, confirmPassword) => {
  const errors = [];

  // Match the backend blank-password check, which treats whitespace-only values as blank.
  if (!password.trim()) {
    errors.push(PASSWORD_BLANK);
  }

  // Match the backend minimum password length policy.
  if (password.length < MIN_PASSWORD_LENGTH) {
    errors.push(PASSWORD_TOO_SHORT);
  }

  // Match the backend maximum password length policy.
  if (password.length > MAX_PASSWORD_LENGTH) {
    errors.push(PASSWORD_TOO_LONG);
  }

  // Confirm the user typed the same password twice before calling the API.
  if (password !== confirmPassword) {
    errors.push(PASSWORD_MISMATCH);
  }

  return errors;
};

/**
 * RegisterPage Component - A form for invited users to accept their registration invite.
 */
const RegisterPage = () => {
  // State hooks to manage invite validation data and form input.
  const [email, setEmail] = useState(''); // Store invitee email returned by token validation
  const [organisation, setOrganisation] = useState(''); // Store invitee organisation returned by token validation
  const [fullName, setFullName] = useState(''); // Store user-entered full name
  const [password, setPassword] = useState(''); // Store user-entered password
  const [confirmPassword, setConfirmPassword] = useState(''); // Store password confirmation

  // State hooks to manage token validation, form validation, and submission feedback.
  const [error, setError] = useState(''); // Store accept-invite endpoint errors
  const [passwordErrors, setPasswordErrors] = useState([]); // Store password policy validation errors
  const [tokenError, setTokenError] = useState(''); // Store missing, expired, or invalid token errors
  const [isTokenChecking, setIsTokenChecking] = useState(true); // State to track invite token validation
  const [isSubmitting, setIsSubmitting] = useState(false); // State to track accept-invite submission
  const [isSigningOutForInvite, setIsSigningOutForInvite] = useState(false); // State to track sign-out before accepting an invite

  const { token: accessToken, login, logout } = useContext(AuthContext); // Use the current session token and auth helpers from AuthContext
  const navigate = useNavigate(); // Hook to navigate to the app after successful registration
  const [searchParams] = useSearchParams(); // Hook to read the token query parameter from the emailed invite link
  const inviteToken = searchParams.get('token'); // Store invite token from /register?token=
  const isLoggedIn = Boolean(accessToken); // Track whether the user already has an active app session

  /**
   * useEffect - Validates the invite token when the registration page loads.
   */
  useEffect(() => {
    const validateInviteToken = async () => {
      if (isLoggedIn) {
        setIsTokenChecking(false);
        return;
      }

      if (!inviteToken) {
        setTokenError('This registration link is missing a token. Please ask for a new invite.');
        setIsTokenChecking(false);
        return;
      }

      try {
        // Validate the emailed invite token and prefill locked registration fields.
        const response = await axios.get(`${API_BASE_URL}/api/v1/invites/${encodeURIComponent(inviteToken)}/validate`);
        setEmail(response.data?.email || '');
        setOrganisation(response.data?.org_name || '');
        setTokenError('');
      } catch (err) {
        setTokenError(getRegisterErrorMessage(err));
      } finally {
        setIsTokenChecking(false);
      }
    };

    validateInviteToken();
  }, [inviteToken, isLoggedIn]);

  /**
   * handleSignOutAndAcceptInvite - Signs out the current user while keeping the invite link open.
   */
  const handleSignOutAndAcceptInvite = async () => {
    const currentAccessToken = localStorage.getItem('access_token'); // Read the latest access token for logout
    const currentRefreshToken = localStorage.getItem('refresh_token'); // Read the latest refresh token for logout

    setError('');
    setTokenError('');
    setIsSigningOutForInvite(true);
    setIsTokenChecking(Boolean(inviteToken));

    try {
      if (currentAccessToken && currentRefreshToken) {
        // Revoke the current browser session before showing the invite registration form.
        await axios.post(`${API_BASE_URL}/api/v1/auth/logout`, {
          access_token: currentAccessToken,
          refresh_token: currentRefreshToken,
        });
      }
    } catch (err) {
      // Continue clearing local auth even if backend logout cannot revoke the old tokens.
      setError(getRegisterErrorMessage(err));
    } finally {
      logout(); // Clear local auth so the invite token can be validated for the invited user
      setIsSigningOutForInvite(false);
    }
  };

  /**
   * handleSubmit - Accepts the invite and logs the user in with returned tokens.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Run frontend password checks before calling the backend.
    const validationErrors = validatePassword(password, confirmPassword);
    setPasswordErrors(validationErrors);

    if (validationErrors.length > 0 || tokenError || isLoggedIn) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit full name and password to the FastAPI invite accept endpoint.
      const response = await axios.post(`${API_BASE_URL}/api/v1/invites/${encodeURIComponent(inviteToken)}/accept`, {
        full_name: fullName,
        password,
      });

      // Check if the response contains the JWT tokens and log the user in.
      if (response.data.access_token && response.data.refresh_token) {
        // Store JWT tokens in localStorage.
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);

        // Call the login function from context to update the app state.
        login(response.data.access_token, email);

        // Navigate to the home page upon successful invite acceptance.
        navigate('/');
      }
    } catch (err) {
      // Handle errors if the invite acceptance attempt fails.
      setError(getRegisterErrorMessage(err));

      // Clear password inputs after a failed registration attempt.
      setPassword('');
      setConfirmPassword('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // Main container to center the invite registration form on the screen.
    <Container className="d-flex justify-content-center align-items-center min-vh-100">
      <Row className="w-100">
        <Col md={7} lg={5} className="mx-auto">
          {/* Card component for displaying the invite registration form. */}
          <Card className="shadow-sm">
            <Card.Body>
              {/* Logo and heading. */}
              <div className="text-center mb-4">
                <img
                  alt="AGRISMART logo"
                  src="/android-chrome-192x192.png"
                  width="60"
                  height="60"
                  className="text-primary"
                />
                <h2 className="mt-2">Create account</h2>
              </div>

              {/* Show an already-signed-in state without invalidating the current session. */}
              {isLoggedIn && (
                <div className="text-center">
                  <Alert variant="info">
                    <h5 className="mb-2">You are already signed in</h5>
                    <div>To accept this invite, sign out of the current account first.</div>
                  </Alert>

                  {/* Let the signed-in user continue without sending them back to login. */}
                  <Button as={Link} to="/" variant="outline-secondary" className="w-100 mb-2" disabled={isSigningOutForInvite}>
                    Continue to app
                  </Button>

                  {/* Let a different user accept the invite in this browser after the current session is cleared. */}
                  {inviteToken && (
                    <Button variant="primary" className="w-100" onClick={handleSignOutAndAcceptInvite} disabled={isSigningOutForInvite}>
                      {isSigningOutForInvite ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Signing out
                        </>
                      ) : (
                        'Sign out and accept invite'
                      )}
                    </Button>
                  )}
                </div>
              )}

              {/* Show loading feedback while checking whether the emailed invite token is valid. */}
              {!isLoggedIn && isTokenChecking && (
                <Alert variant="info" className="text-center">
                  Checking invite link...
                </Alert>
              )}

              {/* Show a dead-end registration link state when the token is missing, expired, or invalid. */}
              {!isLoggedIn && !isTokenChecking && tokenError && (
                <div className="text-center">
                  <Alert variant="danger">
                    <h5 className="mb-2">Registration link unavailable</h5>
                    <div>{tokenError}</div>
                  </Alert>

                  {/* Let the signed-out user return to login if this invite link cannot be used. */}
                  <Button as={Link} to="/login" variant="primary" className="w-100">
                    Back to login
                  </Button>
                </div>
              )}

              {/* Form for invite acceptance after the token has been validated. */}
              {!isLoggedIn && !isTokenChecking && !tokenError && (
                <Form onSubmit={handleSubmit}>
                  {/* Locked email field prefilled from the invite token validation endpoint. */}
                  <Form.Group controlId="register-email" className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control type="email" value={email} disabled readOnly />
                  </Form.Group>

                  {/* Locked organisation field prefilled from the invite token validation endpoint. */}
                  <Form.Group controlId="register-organisation" className="mb-3">
                    <Form.Label>Organisation</Form.Label>
                    <Form.Control type="text" value={organisation} disabled readOnly />
                  </Form.Group>

                  {/* Full name input field. */}
                  <Form.Group controlId="register-full-name" className="mb-3">
                    <Form.Label>Full name</Form.Label>
                    <Form.Control
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                      autoComplete="name"
                      placeholder="Enter full name"
                    />
                  </Form.Group>

                  {/* New password input field. */}
                  <Form.Group controlId="register-password" className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={MIN_PASSWORD_LENGTH}
                      maxLength={MAX_PASSWORD_LENGTH}
                      autoComplete="new-password"
                      placeholder="Enter password"
                    />
                  </Form.Group>

                  {/* Confirm password input field. */}
                  <Form.Group controlId="register-confirm-password" className="mb-3">
                    <Form.Label>Confirm password</Form.Label>
                    <Form.Control
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={MIN_PASSWORD_LENGTH}
                      maxLength={MAX_PASSWORD_LENGTH}
                      autoComplete="new-password"
                      placeholder="Confirm password"
                    />
                  </Form.Group>

                  {/* Display password policy errors if any. */}
                  {passwordErrors.length > 0 && (
                    <Alert variant="danger">
                      {passwordErrors.map((passwordError) => (
                        <div key={passwordError}>{PASSWORD_ERROR_MESSAGES[passwordError]}</div>
                      ))}
                    </Alert>
                  )}

                  {/* Display invite acceptance endpoint error message if any. */}
                  {error && <Alert variant="danger">{error}</Alert>}

                  {/* Submit button. */}
                  <Button variant="primary" type="submit" className="w-100" disabled={isSubmitting || isTokenChecking || Boolean(tokenError)}>
                    {isSubmitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Creating account
                      </>
                    ) : (
                      'Create account'
                    )}
                  </Button>

                  {/* Login navigation. */}
                  <div className="text-center mt-3">
                    <Button as={Link} to="/login" variant="link" className="p-0">
                      Back to login
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default RegisterPage;
